import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  DeleteAccountRequest,
  ChangePasswordRequest,
  ChangeEmailRequest,
  ChangeEmailResponse,
} from '@mockd/shared';
import { getApiDirectUrl } from '../config';
import { api, ApiError } from '../utils/apiClient';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  /** Set when the session check itself failed (API down), as opposed to signed out. */
  sessionError: string | null;
  retrySession: () => Promise<void>;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  sendMagicLink: (email: string, redirect?: string) => Promise<void>;
  resetMagicLink: () => void;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  refreshUser: () => Promise<void>;
  loginWithGitHub: (redirect?: string) => void;
  deleteAccount: (data: DeleteAccountRequest) => Promise<void>;
  changePassword: (data: ChangePasswordRequest) => Promise<void>;
  /** Resolves to the address the confirmation link was sent to. */
  changeEmail: (data: ChangeEmailRequest) => Promise<string>;
  confirmEmailChange: (token: string) => Promise<void>;
  magicLinkSent: boolean;
  magicLinkEmail: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState<string | null>(null);

  const [sessionError, setSessionError] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    const result = await api.get<{ user: User | null }>('/api/auth/me');
    setUser(result.user);
  }, []);

  // A signed-out visitor gets `200 { user: null }` from /me, so a throw here means
  // the API is unreachable or failing — not that the session is gone.
  const checkSession = useCallback(async () => {
    setLoading(true);
    try {
      await refreshUser();
      setSessionError(null);
    } catch (err) {
      setSessionError(err instanceof ApiError ? err.message : 'Unable to reach the server');
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  // Check if user is already logged in on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (data: LoginRequest) => {
    setError(null);
    setLoading(true);

    try {
      const result = await api.post<{ user: User }>('/api/auth/login', data);
      setUser(result.user);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (data: RegisterRequest) => {
    setError(null);
    setLoading(true);

    try {
      const result = await api.post<{ user: User }>('/api/auth/register', data);
      setUser(result.user);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Registration failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      setUser(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const sendMagicLink = useCallback(async (email: string, redirect?: string) => {
    setError(null);
    setLoading(true);

    try {
      await api.post('/api/auth/magic-link/send', { email, redirect });
      setMagicLinkSent(true);
      setMagicLinkEmail(email);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to send magic link';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetMagicLink = useCallback(() => {
    setMagicLinkSent(false);
    setMagicLinkEmail(null);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    setError(null);
    setLoading(true);

    try {
      await api.post('/api/auth/password/request-reset', { email });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to send reset link';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      const result = await api.post<{ user: User }>('/api/auth/password/reset', { token, password });
      setUser(result.user);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to reset password';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyEmail = useCallback(async (token: string) => {
    setError(null);
    setLoading(true);

    try {
      await api.post('/api/auth/verify-email', { token });
      await refreshUser();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to verify email';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  // Leaves the global loading/error alone: its only caller is the banner, which
  // unmounts while `loading` is set and reports failures itself.
  const resendVerification = useCallback(async () => {
    await api.post('/api/auth/verify-email/resend');
  }, []);

  const loginWithGitHub = useCallback((redirect?: string) => {
    const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
    window.location.href = `${getApiDirectUrl()}/api/auth/github${params}`;
  }, []);

  // Leaves the global loading/error alone, like resendVerification: the modal
  // that calls it reports failures itself, and needs the error code to do so.
  const deleteAccount = useCallback(async (data: DeleteAccountRequest) => {
    await api.del('/api/auth/me', data);
    setUser(null);
  }, []);

  // Same as deleteAccount: the modal reports failures and reads the error code.
  const changePassword = useCallback(async (data: ChangePasswordRequest) => {
    const result = await api.put<{ user: User }>('/api/auth/me/password', data);
    setUser(result.user);
  }, []);

  // Same as deleteAccount: callers report failures and read the error code.
  const changeEmail = useCallback(async (data: ChangeEmailRequest) => {
    const result = await api.put<ChangeEmailResponse>('/api/auth/me/email', data);
    return result.pendingEmail;
  }, []);

  const confirmEmailChange = useCallback(async (token: string) => {
    const result = await api.post<{ user: User }>('/api/auth/me/email/confirm', { token });
    setUser(result.user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, sessionError, retrySession: checkSession, login, register, logout, clearError, sendMagicLink, resetMagicLink, requestPasswordReset, resetPassword, verifyEmail, resendVerification, refreshUser, loginWithGitHub, deleteAccount, changePassword, changeEmail, confirmEmailChange, magicLinkSent, magicLinkEmail }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
